import { useEffect, useState } from "react";
import { FiCreditCard } from "react-icons/fi";
import api from "../services/api";

const BankDetails = () => {
    const [banks, setBanks] = useState([]);
    const [formData, setFormData] = useState({
        bank_id: "",
        account_name: "",
        account_number: "",
    });
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [submitErrors, setSubmitErrors] = useState([]);
    const [submitSuccess, setSubmitSuccess] = useState("");
    const bankLengthRules = {
        "Asia United Bank Corporation": ["10-16"],
        "Bank of Commerce": ["12"],
        "BDO Network Bank": ["12"],
        "BDO Unibank Inc": ["12"],
        BPI: ["10-12"],
        "China Bank Savings Inc": ["8-12"],
        "China Banking Corporation": ["10-12"],
        "CIMB Bank Philippines Inc": ["14"],
        "Development Bank of the Philippines": ["10"],
        "East West Banking Corporation": ["12"],
        "Equicom Savings Bank": ["10"],
        "G-Xchange Inc": ["10-20"],
        "Gotyme Bank Corporation": ["12"],
        "Land Bank of the Philippines": ["10"],
        "Malayan Savings Bank Inc.": ["11-12"],
        "MariBank Philippines": ["11"],
        "Maya Bank Inc": ["12-20"],
        "Maya Philippines Inc./Maya Wallet": ["11"],
        "Maybank Philippines Incorporated": ["11"],
        "Metropolitan Bank and Trust Company": ["13"],
        "Philippine Bank of Communications": ["12"],
        "Philippine Business Bank Inc": ["12", "13", "15", "16"],
        "Philippine National Bank": ["10", "12", "16"],
        "Philippine Savings Bank": ["12", "15"],
        "Philippine Veterans Bank": ["13"],
        "Rizal Commercial Banking Corporation": ["10", "15", "16"],
        "Robinsons Bank": ["12", "15", "16"],
        "Security Bank Corporation": ["13"],
        "Standard Chartered Bank": ["6-19"],
        "Sterling Bank of Asia": ["12-16"],
        "UCPB Savings Bank Inc": ["11"],
        "Union Bank of the Philippines": [], // NA
        "Wealth Development Bank Corporation": ["12"],
    };

    useEffect(() => {
        const fetchBanks = async () => {
            try {
                const token = localStorage.getItem("rmc2.access_token");
                if (!token) {
                    setSubmitErrors(["Missing access token."]);
                    return;
                }

                const response = await api.get("/banks", {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (response.data?.status === "success") {
                    setBanks(response.data?.data || []);
                }
            } catch (error) {
                setSubmitErrors(["Failed to load banks."]);
            }
        };

        const fetchBankDetails = async () => {
            setIsLoading(true);
            setSubmitErrors([]);
            setSubmitSuccess("");
            try {
                const token = localStorage.getItem("rmc2.access_token");
                if (!token) {
                    setSubmitErrors(["Missing access token."]);
                    return;
                }

                const response = await api.get("/bank-details", {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });

                if (
                    response.data?.status === "success" &&
                    response.data?.data
                ) {
                    const data = response.data.data;
                    setFormData({
                        bank_id: data.bank_id ? String(data.bank_id) : "",
                        account_name: data.account_name || "",
                        account_number: data.account_number || "",
                    });
                }
            } catch (error) {
                setSubmitErrors(["Failed to load bank details."]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchBanks();
        fetchBankDetails();
    }, []);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setIsSaving(true);
        setSubmitErrors([]);
        setSubmitSuccess("");

        try {
            const token = localStorage.getItem("rmc2.access_token");
            if (!token) {
                setSubmitErrors(["Missing access token."]);
                return;
            }

            const selectedBank = banks.find(
                (bank) => String(bank.id) === String(formData.bank_id),
            );
            const accountNumber = String(formData.account_number || "").replace(
                /\D/g,
                "",
            );
            const bankRules = selectedBank
                ? bankLengthRules[selectedBank.name]
                : null;

            if (accountNumber.length === 0) {
                setSubmitErrors(["Account number is required."]);
                return;
            }

            if (accountNumber !== formData.account_number) {
                setSubmitErrors(["Account number must contain digits only."]);
                return;
            }

            if (bankRules && !bankRules.includes("NA")) {
                const isValid = bankRules.some((rule) => {
                    if (rule.includes("-")) {
                        const [min, max] = rule.split("-").map(Number);
                        return (
                            accountNumber.length >= min &&
                            accountNumber.length <= max
                        );
                    }
                    return accountNumber.length === Number(rule);
                });

                if (!isValid) {
                    setSubmitErrors([
                        `Account number length for ${selectedBank?.name || "selected bank"} must be ${bankRules.join(
                            ", ",
                        )} digits.`,
                    ]);
                    return;
                }
            }

            const response = await api.put(
                "/bank-details",
                { ...formData, account_number: accountNumber },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                },
            );

            if (response.data?.status !== "success") {
                const message =
                    response.data?.message || "Failed to update bank details.";
                if (message && typeof message === "object") {
                    const flattened = Object.values(message).flat();
                    setSubmitErrors(flattened);
                } else {
                    setSubmitErrors([message]);
                }
                return;
            }

            setSubmitSuccess(response.data?.message || "Bank details updated.");
        } catch (error) {
            const message = error?.response?.data?.message;
            if (message && typeof message === "object") {
                const flattened = Object.values(message).flat();
                setSubmitErrors(flattened);
            } else if (typeof message === "string") {
                setSubmitErrors([message]);
            } else {
                setSubmitErrors(["Failed to update bank details."]);
            }
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="mx-auto w-full max-w-[1200px]">
            <div className="rounded-3xl border border-white/60 bg-white/90 p-8 shadow-lg shadow-slate-200/60">
                <div className="flex flex-wrap items-center gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-100 text-[var(--brand-blue)]">
                        <FiCreditCard className="text-xl" />
                    </div>
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                            Banking
                        </p>
                        <h1 className="mt-2 text-3xl font-semibold text-slate-900">
                            Bank Details
                        </h1>
                        <p className="mt-2 text-sm text-slate-500">
                            Keep your payout account updated to receive
                            incentives faster.
                        </p>
                    </div>
                </div>

                <form
                    onSubmit={handleSubmit}
                    className="mt-8 max-w-lg space-y-5"
                >
                    <div className="flex flex-col gap-2">
                        <label className="text-xs font-semibold text-slate-500">
                            Bank Name
                        </label>
                        <select
                            name="bank_id"
                            value={formData.bank_id}
                            onChange={handleChange}
                            className={`w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm shadow-sm transition focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200 ${
                                formData.bank_id
                                    ? "text-slate-700"
                                    : "text-slate-400"
                            }`}
                        >
                            <option value="">Choose Bank</option>
                            {banks.map((bank) => (
                                <option
                                    key={bank.id}
                                    value={bank.id}
                                    className="text-slate-700"
                                >
                                    {bank.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="flex flex-col gap-2">
                        <label className="text-xs font-semibold text-slate-500">
                            Account Name
                        </label>
                        <input
                            type="text"
                            name="account_name"
                            value={formData.account_name}
                            onChange={handleChange}
                            placeholder="Account Name"
                            className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm transition placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                        />
                    </div>

                    <div className="flex flex-col gap-2">
                        <label className="text-xs font-semibold text-slate-500">
                            Account Number
                        </label>
                        <input
                            type="text"
                            name="account_number"
                            value={formData.account_number}
                            onChange={(event) => {
                                const digits = event.target.value.replace(
                                    /\D/g,
                                    "",
                                );
                                setFormData((prev) => ({
                                    ...prev,
                                    account_number: digits,
                                }));
                            }}
                            placeholder="Account Number"
                            className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm transition placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                        />
                    </div>

                    {submitErrors.length > 0 && (
                        <div className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-600">
                            <ul className="space-y-1">
                                {submitErrors.map((item, index) => (
                                    <li key={`${item}-${index}`}>{item}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {submitSuccess && (
                        <div className="rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-600">
                            {submitSuccess}
                        </div>
                    )}

                    <div className="flex justify-end">
                        <button
                            type="submit"
                            disabled={isSaving || isLoading}
                            className="rounded-2xl bg-[var(--brand-orange)] px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-orange-200 transition hover:bg-[var(--brand-orange-dark)]"
                        >
                            {isSaving ? "Saving..." : "Save Bank Details"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default BankDetails;
